import {
    Dialog,
    DialogContent,
    Divider,
    Grid
} from '@mui/material';
import { WithStyles, useTheme, withStyles } from '@mui/styles';
import { CancelButtonWhite } from '@tassat/button';
import { PledgeTransactionTypeEnum, PledgeTransactionTypeString } from '@tassat/common-definitions';
import { SecurityCurrencyAssetPair } from '@tassat/fred-extended-definitions';
import {
    Failure as FailureIcon,
    Spinner as SpinnerIcon,
    Success as SuccessIcon
} from '@tassat/icon';
import { capitalizeFirstLetter } from '@tassat/utility';
import React from 'react';

import hourGlass from 'Images/hourglass.svg';
import { handleOnClose } from 'Lib/materialUiHelper';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { ValidationData, ValidationState } from 'Reducers/validation';
import { getSecurityCurrencyPairBySecurityTicker } from 'Selectors/assets';
import { getValidation } from 'Selectors/common';
import { getFirstFirmName } from 'Selectors/firms';
import {
    TransactionProcessing,
    TransactionTimeout
} from 'SharedComponents/transactions/modals';

import { ModalPledgeValues, PledgeModalAction } from './pledgeDetailsModal/pledgeContextWorkflow';
import { PledgeResultFailed } from './pledgeResultFailed';
import { PledgeResultSuccess } from './pledgeResultSuccess';

export interface PledgeResultModalProps extends WithStyles<typeof muiStyles> {
    modalPledgeValues: ModalPledgeValues | null;
    pledgeType: PledgeTransactionTypeEnum;
    pledgeValidationType: string;
    actionType?: PledgeModalAction;
    open: boolean;
    onClose: () => void;
}

interface DetermineTransactionBody extends PledgeResultModalProps {
    assetPair: SecurityCurrencyAssetPair;
    validation?: ValidationData;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: {
        minWidth: '540px',
        padding: '.5rem'
    },
    headerContainer: {
        position: 'relative' as const,
        height: '10rem',
        backgroundColor: theme?.app?.clientTheme.colors.primaryColorLight ||
            '#009BDF'
    },
    headerIcon: {
        height: '86px',
        width: '86px'
    },
    cancelIcon: {
        position: 'absolute' as const,
        top: 18,
        right: 18
    },
    paperWidthXl: { width: '40%' }
});

const determineHeaderIcon = (props: PledgeResultModalProps & { validation?: ValidationData }, theme: ApplicationMuiTheme) => {
    const {
        classes,
        validation
    } = props;

    if (!validation) return null;

    const {
        pending,
        timeout,
        error
    } = validation;

    if (pending) {
        return <SpinnerIcon className={classes.headerIcon} />;
    } else if (timeout) {
        return (
            <img
                alt='time out symbol'
                src={hourGlass}
            />
        );
    } else if (error) {
        return (
            <FailureIcon
                className={classes.headerIcon}
                iconColor={theme?.app?.applicationColors.failure || '#FA5252'}
            />
        );
    } else {
        return (
            <SuccessIcon
                className={classes.headerIcon}
                iconColor={theme?.app?.applicationColors.successLight || '#7DEC62'}
            />
        );
    }
};

const determineTransactionBody = (props: DetermineTransactionBody) => {
    const {
        actionType,
        modalPledgeValues,
        validation,
        onClose,
        assetPair,
        pledgeType
    } = props;

    if (!validation || !modalPledgeValues) return null;

    const {
        pending,
        timeout,
        error
    } = validation;

    if (pending) {
        let progressText = 'Request in progress';
        if (modalPledgeValues.RequestId) {
            const transactionType = PledgeTransactionTypeString[pledgeType];
            progressText = `${capitalizeFirstLetter(transactionType ?? '')} in Progress`;
        } else if (pledgeType) {
            progressText = `${capitalizeFirstLetter(PledgeTransactionTypeString[pledgeType] ?? '')} in Progress`;
        }

        return (
            <TransactionProcessing
                text={progressText}
            />
        );
    } else if (timeout) {
        return (
            <TransactionTimeout />
        );
    } else if (error) {
        return (
            <PledgeResultFailed
                modalPledgeValues={modalPledgeValues}
                onClose={onClose}
                pledgeType={pledgeType}
            />
        );
    } else {
        return (
            <PledgeResultSuccess
                actionType={actionType}
                assetPair={assetPair}
                modalPledgeValues={modalPledgeValues}
                onClose={onClose}
                pledgeType={pledgeType}
                validation={validation}
            />
        );
    }
};

function PledgeResultModalComponent(props: PledgeResultModalProps) {
    const {
        classes,
        modalPledgeValues,
        pledgeValidationType,
        open,
        onClose
    } = props;

    const assetPair = useAppSelector((state) => {
        let ticker = '';

        if (modalPledgeValues) {
            if ('Ticker' in modalPledgeValues) {
                ticker = modalPledgeValues.Ticker;
            } else if ('AssetType' in modalPledgeValues) {
                ticker = modalPledgeValues?.AssetType?.refData?.security?.Ticker ??
                    modalPledgeValues?.AssetType?.refData?.currency?.Ticker ?? '';
            }
        }

        return getSecurityCurrencyPairBySecurityTicker(state, ticker);
    });

    const validation = useAppSelector((state) => {
        return modalPledgeValues && pledgeValidationType ? getValidation(state)[pledgeValidationType as keyof ValidationState] : undefined;
    });

    const firmName = useAppSelector(getFirstFirmName);

    const theme = useTheme<ApplicationMuiTheme>();
    if (!modalPledgeValues) return null;

    return (
        <Dialog
            classes={{ paperWidthXl: classes.paperWidthXl }}
            disableEscapeKeyDown
            maxWidth='xl'
            onClose={() => handleOnClose(onClose)}
            open={open}
            transitionDuration={{
                enter: 0,
                exit: 0
            }}
        >
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    container
                    item
                    justifyContent='center'
                    spacing={0}
                    xs={12}
                >
                    <Grid
                        alignItems='center'
                        className={classes.headerContainer}
                        container
                        direction='row'
                        item
                        justifyContent='center'
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        {determineHeaderIcon({
                            ...props,
                            validation
                        }, theme)}
                    </Grid>
                    {validation && !validation.pending &&
                        <Grid
                            className={classes.cancelIcon}
                            item
                        >
                            <CancelButtonWhite onClick={onClose} />
                        </Grid>}
                </Grid>
                <Divider variant='middle' />
                <Grid
                    item
                    xs={12}
                >
                    <DialogContent>
                        {determineTransactionBody({
                            ...props,
                            assetPair,
                            validation
                        })}
                    </DialogContent>
                </Grid>
            </Grid>
        </Dialog>
    );
}

const PledgeResultModal = withStyles(muiStyles)(PledgeResultModalComponent);
export { PledgeResultModal };